/**
 * Base CSV Processor
 * Shared functionality for all CSV processors
 * Handles file parsing, memory monitoring, error collection, and result formatting
 */

import Papa from 'papaparse'
import type {
  CSVRow,
  BaseUploadContext,
  ContactUploadContext,
  PropertyUploadContext,
} from '@/types/csv-upload'
import { MemoryMonitor } from './utils/memory-monitor'

export interface BaseCSVProcessingOptions {
  skipEmptyLines?: boolean
  trimHeaders?: boolean
  trimValues?: boolean
  skipRows?: number
  maxMemoryMB?: number
}

export interface BaseCSVResult {
  success: boolean
  totalRows: number
  processedRows: number
  skipped: number
  errors: Array<{
    row: number
    message: string
    data?: any
  }>
  warnings: Array<{
    row: number
    message: string
    data?: any
  }>
  memoryUsage?: {
    used: number
    limit: number
    percentage: number
  }
}

export interface BaseCSVPreviewResult extends BaseCSVResult {
  sampleRows: CSVRow[]
  headers: string[]
}

/**
 * Abstract base class for CSV processors
 */
export abstract class BaseCSVProcessor<
  TRecord,
  TResult extends BaseCSVResult,
  TContext extends BaseUploadContext =
    | ContactUploadContext
    | PropertyUploadContext,
> {
  protected context: TContext
  protected options: BaseCSVProcessingOptions
  protected memoryMonitor: MemoryMonitor

  constructor(context: TContext, options: BaseCSVProcessingOptions = {}) {
    this.context = context
    this.options = {
      skipEmptyLines: true,
      trimHeaders: true,
      trimValues: true,
      skipRows: 0,
      maxMemoryMB: 200,
      ...options,
    }
    this.memoryMonitor = new MemoryMonitor({
      limitMB: this.options.maxMemoryMB,
    })
  }

  /**
   * Parse CSV file and return preview data
   */
  async parseCSV(file: File): Promise<TResult> {
    // Check file size before processing
    const fileSizeStatus = this.memoryMonitor.validateFileSize(file)
    if (!fileSizeStatus.safe) {
      return this.createErrorResult(
        [
          {
            row: 0,
            message: fileSizeStatus.message!,
          },
        ],
        fileSizeStatus,
      )
    }

    return new Promise((resolve, reject) => {
      Papa.parse<CSVRow>(file, {
        header: true,
        skipEmptyLines: this.options.skipEmptyLines,
        transformHeader: header => {
          return this.options.trimHeaders ? header.trim() : header
        },
        transform: value => {
          return this.options.trimValues ? value.trim() : value
        },
        complete: results => {
          const preview = this.processCSVResults(results)
          resolve(preview)
        },
        error: error => {
          reject(new Error(`CSV parsing failed: ${error.message}`))
        },
      })
    })
  }

  /**
   * Parse CSV text directly
   */
  parseCSVText(csvText: string): TResult {
    // Check memory before parsing
    const memoryStatus = this.memoryMonitor.checkMemory()
    if (!memoryStatus.safe) {
      return this.createErrorResult(
        [
          {
            row: 0,
            message: memoryStatus.message || 'Memory limit exceeded',
          },
        ],
        memoryStatus,
      )
    }

    const results = Papa.parse<CSVRow>(csvText, {
      header: true,
      skipEmptyLines: this.options.skipEmptyLines,
      transformHeader: header => {
        return this.options.trimHeaders ? header.trim() : header
      },
      transform: value => {
        return this.options.trimValues ? value.trim() : value
      },
    })

    return this.processCSVResults(results)
  }

  /**
   * Process parsed CSV results - template method pattern
   */
  protected processCSVResults(results: Papa.ParseResult<CSVRow>): TResult {
    const errors: BaseCSVResult['errors'] = []
    const warnings: BaseCSVResult['warnings'] = []
    const records: TRecord[] = []
    let skipped = 0

    // Skip header rows if configured
    const dataRows = results.data.slice(this.options.skipRows || 0)

    // Process each row
    dataRows.forEach((row, index) => {
      const rowNumber = index + 1 + (this.options.skipRows || 0)

      // Check memory periodically (every 100 rows)
      if (index % 100 === 0) {
        const memoryStatus = this.memoryMonitor.checkMemory()
        if (!memoryStatus.safe) {
          errors.push({
            row: rowNumber,
            message:
              memoryStatus.message || 'Memory limit exceeded during processing',
          })
          return // Stop processing
        }
      }

      try {
        // Transform the row using subclass implementation
        const record = this.transformRow(row, rowNumber)

        if (record) {
          // Validate the transformed record using subclass implementation
          const validation = this.validateRecord(record, rowNumber)

          if (validation.isValid) {
            records.push(record)

            // Add any warnings
            validation.warnings?.forEach(warning => {
              warnings.push({
                row: rowNumber,
                message: warning,
                data: row,
              })
            })
          } else {
            // Convert validation errors to warnings - skip invalid rows but don't block upload
            skipped++
            validation.errors.forEach(error => {
              warnings.push({
                row: rowNumber,
                message: `Row skipped: ${error}`,
                data: row,
              })
            })

            // Add any validation warnings as well
            validation.warnings?.forEach(warning => {
              warnings.push({
                row: rowNumber,
                message: warning,
                data: row,
              })
            })
          }
        } else {
          const silent = this.context.silentSkip
          this.context.silentSkip = undefined
          if (!silent) {
            skipped++
            const skipReason =
              this.context.skipReason ||
              'Unable to transform row data into valid record (missing required fields)'
            warnings.push({
              row: rowNumber,
              message: `Row skipped: ${skipReason}`,
              data: row,
            })
          }
          this.context.skipReason = undefined
        }
      } catch (error) {
        errors.push({
          row: rowNumber,
          message:
            error instanceof Error
              ? error.message
              : 'Unknown error during transformation',
          data: row,
        })
      }
    })

    // Add parsing errors if any
    results.errors.forEach(error => {
      errors.push({
        row: error.row !== undefined ? error.row + 1 : 0,
        message: error.message,
        data: error,
      })
    })

    // Get final memory usage
    const finalMemoryStatus = this.memoryMonitor.checkMemory()

    // Create result using subclass implementation
    return this.createResult({
      success: errors.length === 0,
      totalRows: results.data.length,
      processedRows: records.length,
      skipped,
      records,
      errors,
      warnings,
      sampleRows: results.data.slice(0, 5),
      headers: results.meta.fields || [],
      memoryUsage: {
        used: finalMemoryStatus.used,
        limit: finalMemoryStatus.limit,
        percentage: finalMemoryStatus.percentage,
      },
    })
  }

  /**
   * Abstract methods to be implemented by subclasses
   */
  protected abstract transformRow(
    row: CSVRow,
    rowNumber: number,
  ): TRecord | null
  protected abstract validateRecord(
    record: TRecord,
    rowNumber: number,
  ): {
    isValid: boolean
    errors: string[]
    warnings: string[]
  }
  protected abstract createResult(data: {
    success: boolean
    totalRows: number
    processedRows: number
    skipped: number
    records: TRecord[]
    errors: BaseCSVResult['errors']
    warnings: BaseCSVResult['warnings']
    sampleRows: CSVRow[]
    headers: string[]
    memoryUsage: {
      used: number
      limit: number
      percentage: number
    }
  }): TResult
  protected abstract createErrorResult(
    errors: BaseCSVResult['errors'],
    memoryStatus: { used: number; limit: number; percentage: number },
  ): TResult

  /**
   * Get memory usage in MB
   */
  getUsageMB(): number {
    return this.memoryMonitor.getUsageMB()
  }
}
